import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import { ClockCounterClockwise, MagicWand } from '@phosphor-icons/react';
import { formatAnyDate } from '../../../utils/dateFormat';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const MILESTONE_COLORS = {
  'Advance Pending': 'bg-red-100 text-red-800',
  'Less than 50% Received': 'bg-orange-100 text-orange-800',
  '50% Advance Completed': 'bg-amber-100 text-amber-800',
  '90% Dispatch Payment Completed': 'bg-blue-100 text-blue-800',
  '100% Payment Completed': 'bg-emerald-100 text-emerald-800',
  'Excess Advance Available': 'bg-violet-100 text-violet-800',
};

export function AdvanceAllocationDialog({ advance, onOpenChange, onDone }) {
  const [receipt, setReceipt] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [remarks, setRemarks] = useState('');
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!advance) return;
    try {
      const [{ data: rd }, { data: eq }] = await Promise.all([
        api.get(`/api/admin/receipt-allocation/receipts/${advance.receipt_number}`),
        api.get(`/api/admin/receipt-allocation/eligible-quotations?customer_mobile=${advance.customer_mobile}`),
      ]);
      setReceipt(rd.receipt);
      setQuotes(eq.quotations || []);
      setAmounts({});
      setConfirming(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load allocation data'); }
  }, [advance]);

  useEffect(() => { load(); }, [load]);

  const available = receipt?.unallocated_amount ?? 0;
  const entries = useMemo(() => Object.entries(amounts).filter(([, v]) => parseFloat(v) > 0), [amounts]);
  const totalEntered = useMemo(() => entries.reduce((s, [, v]) => s + parseFloat(v || 0), 0), [entries]);
  const remaining = Math.round((available - totalEntered) * 100) / 100;

  const rowError = (q) => {
    const v = parseFloat(amounts[q.quotation_number] || 0);
    if (!v) return null;
    if (v < 0) return 'Negative not allowed';
    if (v > q.outstanding_balance + 0.005) return `Exceeds outstanding ${fmtINR(q.outstanding_balance)}`;
    return null;
  };
  const hasErrors = quotes.some((q) => rowError(q)) || totalEntered > available + 0.005;

  const autoAllocate = async () => {
    try {
      const { data } = await api.post('/api/admin/receipt-allocation/auto-preview', { receipt_number: advance.receipt_number });
      const next = {};
      (data.plan || []).forEach((p) => { next[p.quotation_number] = String(p.allocation); });
      setAmounts(next);
      toast.success(`Auto plan: ${fmtINR(data.total_allocated)} across ${data.plan.length} quotation(s), ${fmtINR(data.remaining_unallocated)} stays as advance`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto allocation failed'); }
  };

  const openHistory = async (qn) => {
    setHistoryFor(qn); setHistory(null);
    try {
      const { data } = await api.get(`/api/admin/receipt-allocation/quotations/${encodeURIComponent(qn)}/history`);
      setHistory(data);
    } catch (e) { toast.error('Failed to load history'); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        receipt_number: advance.receipt_number,
        allocations: entries.map(([q, v]) => ({ quotation_no: q, amount: parseFloat(v) })),
        allocation_type: 'manual',
        remarks,
      };
      const { data } = await api.post('/api/admin/receipt-allocation/allocate', payload);
      toast.success(`Allocated ${fmtINR(data.total_allocated_now)} — ${fmtINR(data.remaining_unallocated)} retained as advance`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Allocation failed'); setConfirming(false); }
    setSaving(false);
  };

  if (!advance) return null;
  return (
    <Dialog open={!!advance} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-y-auto" data-testid="advance-allocation-dialog">
        <DialogHeader>
          <DialogTitle>Customer Advance Allocation — {advance.receipt_number}</DialogTitle>
        </DialogHeader>

        {/* Section A — Receipt Details */}
        <div className="grid grid-cols-4 gap-2" data-testid="alloc-section-receipt">
          {[['Customer', `${receipt?.customer_name || advance.customer_name} (${advance.customer_mobile})`],
            ['Receipt Date', formatAnyDate(receipt?.receipt_date)],
            ['Mode / Ref', `${receipt?.payment_mode || ''} ${receipt?.reference ? '· ' + receipt.reference : ''}`],
            ['Total Receipt', fmtINR(receipt?.amount)],
            ['Previously Allocated', fmtINR(receipt?.allocated_amount)],
            ['Available for Allocation', fmtINR(available)],
            ['Status', receipt?.allocation_status || '—'],
            ['Bank', receipt?.received_bank || '—']].map(([k, v]) => (
            <div key={k} className="p-2 rounded-sm border border-zinc-200 bg-zinc-50">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
              <div className="text-sm font-bold truncate">{v}</div>
            </div>
          ))}
        </div>

        {/* Section B — Eligible Quotations */}
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold">
            Eligible Outstanding Quotations (linked to Job Orders) — {quotes.length}
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs border-violet-400 text-violet-700"
            onClick={autoAllocate} data-testid="auto-allocate-btn">
            <MagicWand size={14} className="mr-1" /> Auto Allocate
          </Button>
        </div>
        {quotes.length === 0 ? (
          <div className="p-5 text-center text-sm text-zinc-400 border border-dashed border-zinc-300 rounded-sm">
            No eligible outstanding quotations for this customer. The full receipt stays as unallocated advance for future orders.
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="min-w-full text-xs">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-2 text-left">Quotation</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Linked JOs</th>
                  <th className="p-2 text-right">Value</th>
                  <th className="p-2 text-right">Received</th>
                  <th className="p-2 text-right">Outstanding</th>
                  <th className="p-2 text-left">Milestone</th>
                  <th className="p-2 text-right">Days</th>
                  <th className="p-2 text-right w-32">Allocate Now</th>
                  <th className="p-2 text-right">After</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => {
                  const v = parseFloat(amounts[q.quotation_number] || 0);
                  const err = rowError(q);
                  return (
                    <tr key={q.quotation_number} className={`border-b border-zinc-100 ${v > 0 ? 'bg-blue-50' : ''}`} data-testid={`alloc-q-${q.quotation_number}`}>
                      <td className="p-2 font-mono font-bold text-blue-700">{q.quotation_number}</td>
                      <td className="p-2">{formatAnyDate(q.quotation_date)}</td>
                      <td className="p-2">
                        {(q.jo_numbers || []).length > 0
                          ? (q.jo_numbers || []).map((j) => <div key={j} className="font-mono text-[10px]">{j}</div>)
                          : <Badge className="bg-amber-100 text-amber-800 text-[9px]">DRAFT</Badge>}
                        <div className="text-[9px] text-zinc-500">{(q.jo_statuses || []).join(', ')}</div>
                      </td>
                      <td className="p-2 text-right">{fmtINR(q.converted_value)}</td>
                      <td className="p-2 text-right text-emerald-700">{fmtINR(q.total_received)}</td>
                      <td className="p-2 text-right font-bold text-red-700">{fmtINR(q.outstanding_balance)}</td>
                      <td className="p-2"><Badge className={`text-[9px] ${MILESTONE_COLORS[q.milestone_status] || 'bg-zinc-100'}`}>{q.milestone_status}</Badge></td>
                      <td className="p-2 text-right">{q.days_outstanding ?? '—'}</td>
                      <td className="p-2">
                        <Input type="number" className={`h-7 text-xs text-right ${err ? 'border-red-500' : ''}`}
                          value={amounts[q.quotation_number] || ''}
                          onChange={(e) => setAmounts({ ...amounts, [q.quotation_number]: e.target.value })}
                          data-testid={`alloc-amt-${q.quotation_number}`} />
                        {err && <div className="text-[9px] text-red-600">{err}</div>}
                        <div className="flex gap-1 mt-0.5">
                          <button className="text-[9px] text-blue-600 hover:underline"
                            onClick={() => setAmounts({ ...amounts, [q.quotation_number]: String(Math.min(q.outstanding_balance, Math.max(0, remaining + v))) })}>Full</button>
                          <button className="text-[9px] text-zinc-500 hover:underline"
                            onClick={() => setAmounts({ ...amounts, [q.quotation_number]: '' })}>Clear</button>
                        </div>
                      </td>
                      <td className="p-2 text-right font-bold">{v > 0 ? fmtINR(Math.max(0, q.outstanding_balance - v)) : '—'}</td>
                      <td className="p-2">
                        <Button size="sm" variant="ghost" className="h-6 text-[10px]" title="View Receipt History"
                          onClick={() => openHistory(q.quotation_number)} data-testid={`history-${q.quotation_number}`}>
                          <ClockCounterClockwise size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Section C — Allocation Summary */}
        <div className="grid grid-cols-5 gap-2 mt-2 p-3 rounded-sm border border-zinc-300 bg-zinc-50" data-testid="alloc-section-summary">
          {[['Receipt Available', fmtINR(available)],
            ['Quotations Selected', entries.length],
            ['Total Allocating', fmtINR(totalEntered)],
            ['Remaining Unallocated', fmtINR(remaining)],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
              <div className={`text-lg font-bold ${k === 'Remaining Unallocated' ? (remaining < 0 ? 'text-red-600' : 'text-emerald-700') : ''}`}>{v}</div>
            </div>
          ))}
          <div className="flex items-end justify-end">
            <Button className="bg-emerald-600 hover:bg-emerald-700"
              disabled={entries.length === 0 || hasErrors || saving}
              onClick={() => setConfirming(true)} data-testid="review-allocation-btn">
              Review & Confirm
            </Button>
          </div>
          {totalEntered > available + 0.005 && (
            <div className="col-span-5 text-xs text-red-600 font-bold">Total allocation exceeds the available receipt balance.</div>
          )}
          {remaining > 0.005 && entries.length > 0 && (
            <div className="col-span-5 text-xs text-amber-700">{fmtINR(remaining)} will remain as unallocated customer advance for future orders.</div>
          )}
        </div>
        <div>
          <Input placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="alloc-remarks" className="text-xs" />
        </div>

        {/* Confirmation overlay */}
        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent className="max-w-lg bg-white" data-testid="alloc-confirm-dialog">
            <DialogHeader><DialogTitle>Confirm Allocation — {advance.receipt_number}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>Total Receipt: <b>{fmtINR(receipt?.amount)}</b></div>
                <div>Previously Allocated: <b>{fmtINR(receipt?.allocated_amount)}</b></div>
                <div>This Allocation: <b className="text-blue-700">{fmtINR(totalEntered)}</b></div>
                <div>Remaining Advance: <b className="text-emerald-700">{fmtINR(remaining)}</b></div>
              </div>
              <table className="min-w-full text-xs border border-zinc-200">
                <thead className="bg-zinc-100"><tr><th className="p-1.5 text-left">Quotation</th><th className="p-1.5 text-right">Before</th><th className="p-1.5 text-right">Allocating</th><th className="p-1.5 text-right">After</th></tr></thead>
                <tbody>
                  {entries.map(([qn, v]) => {
                    const q = quotes.find((x) => x.quotation_number === qn);
                    return (
                      <tr key={qn} className="border-t border-zinc-100">
                        <td className="p-1.5 font-mono">{qn}</td>
                        <td className="p-1.5 text-right">{fmtINR(q?.outstanding_balance)}</td>
                        <td className="p-1.5 text-right font-bold text-blue-700">{fmtINR(v)}</td>
                        <td className="p-1.5 text-right">{fmtINR(Math.max(0, (q?.outstanding_balance || 0) - parseFloat(v)))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirming(false)}>Edit Allocation</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={submit} data-testid="confirm-allocation-btn">
                  {saving ? 'Saving…' : 'Confirm Allocation'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Receipt history sub-dialog */}
        <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
          <DialogContent className="max-w-3xl bg-white max-h-[70vh] overflow-y-auto" data-testid="quotation-history-dialog">
            <DialogHeader><DialogTitle>Receipt History — {historyFor}</DialogTitle></DialogHeader>
            {!history ? <div className="p-4 text-center text-zinc-400 text-sm">Loading…</div> : (
              <div className="space-y-2">
                <div className="text-xs">Total received: <b className="text-emerald-700">{fmtINR(history.total_received)}</b></div>
                <table className="min-w-full text-xs border border-zinc-200">
                  <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                    <tr><th className="p-1.5 text-left">Receipt #</th><th className="p-1.5 text-left">Date</th><th className="p-1.5 text-right">Amount</th><th className="p-1.5 text-left">Mode</th><th className="p-1.5 text-left">From Advance</th><th className="p-1.5 text-left">By</th><th className="p-1.5 text-left">Status</th></tr>
                  </thead>
                  <tbody>
                    {(history.receipts || []).map((r) => (
                      <tr key={r.receipt_number} className={`border-t border-zinc-100 ${r.is_reversed ? 'opacity-50 line-through' : ''}`}>
                        <td className="p-1.5 font-mono">{r.receipt_number}</td>
                        <td className="p-1.5">{formatAnyDate(r.receipt_date)}</td>
                        <td className="p-1.5 text-right font-bold">{fmtINR(r.amount)}</td>
                        <td className="p-1.5">{r.payment_mode}</td>
                        <td className="p-1.5 font-mono text-[10px]">{r.source_advance_receipt || '—'}</td>
                        <td className="p-1.5">{r.allocated_by}</td>
                        <td className="p-1.5">{r.is_reversed ? <Badge className="bg-red-100 text-red-700 text-[9px]">REVERSED</Badge> : <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">ACTIVE</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
